import React, { useState, useRef } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { Link } from 'react-router-dom'
import { generatePDF } from '../../lib/pdfExport'

const TOC_SECTIONS = [
  { id: 'overview', key: 'sections.overview.title' },
  { id: 'requirements', key: 'sections.requirements.title' },
  { id: 'foundations', key: 'sections.foundations.title' },
  { id: 'procedures', key: 'sections.procedures.title' },
  { id: 'assessments', key: 'sections.assessments.title' },
  { id: 'interpretation', key: 'sections.interpretation.title' },
  { id: 'report', key: 'sections.report.title' },
  { id: 'profile', key: 'sections.profile.title' },
  { id: 'clinical', key: 'sections.clinical.title' },
  { id: 'troubleshooting', key: 'sections.troubleshooting.title' },
]

function SectionHeading({ id, number, tKey, t }) {
  return (
    <h2 id={id} className="font-display text-xl font-semibold text-gray-800 mt-10 mb-4 scroll-mt-6">
      {number}. {t(tKey)}
    </h2>
  )
}

function SubHeading({ children }) {
  return <h3 className="font-display text-base font-semibold text-gray-700 mt-6 mb-3">{children}</h3>
}

function P({ children }) {
  return <p className="text-gray-600 leading-relaxed mb-3">{children}</p>
}

function BulletList({ items }) {
  return (
    <ul className="list-disc list-outside ml-6 space-y-1.5 text-gray-600 mb-4">
      {items.map((item, i) => <li key={i} className="leading-relaxed">{item}</li>)}
    </ul>
  )
}

function NumberedList({ items }) {
  return (
    <ol className="list-decimal list-outside ml-6 space-y-1.5 text-gray-600 mb-4">
      {items.map((item, i) => <li key={i} className="leading-relaxed">{item}</li>)}
    </ol>
  )
}

/* PDF-friendly list components — render markers as text to avoid html2canvas issues */
function PdfBulletList({ items, className = '' }) {
  return (
    <div className={`ml-6 space-y-1.5 text-gray-600 mb-4 ${className}`}>
      {items.map((item, i) => (
        <div key={i} className="flex leading-relaxed">
          <span className="mr-2.5 shrink-0 mt-0.5">{'\u2022'}</span>
          <span>{item}</span>
        </div>
      ))}
    </div>
  )
}

function PdfNumberedList({ items, start = 1 }) {
  return (
    <div className="ml-6 space-y-1.5 text-gray-600 mb-4">
      {items.map((item, i) => (
        <div key={i} className="flex leading-relaxed">
          <span className="mr-2 shrink-0 tabular-nums">{start + i}.</span>
          <span>{item}</span>
        </div>
      ))}
    </div>
  )
}

function DataTable({ headers, rows, className = '' }) {
  return (
    <div className={`overflow-x-auto mb-4 ${className}`}>
      <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
        <thead>
          <tr className="bg-primary-600 text-white">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-2.5 font-semibold">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2 text-gray-700 border-t border-gray-100">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ManualContent({ t }) {
  return (
    <>
      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="font-display text-3xl font-semibold text-gray-800 mb-2">{t('title')}</h1>
        <p className="text-gray-500 text-sm">{t('subtitle')}</p>
      </div>

      {/* Table of Contents */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
        <h2 className="font-display text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">{t('toc')}</h2>
        <div className="flex gap-8">
          <div className="space-y-1.5 flex-1">
            {TOC_SECTIONS.slice(0, 5).map((s, i) => (
              <span key={s.id} data-toc-target={s.id} className="block text-sm text-primary-600 py-0.5">
                {i + 1}. {t(s.key)}
              </span>
            ))}
          </div>
          <div className="space-y-1.5 flex-1">
            {TOC_SECTIONS.slice(5).map((s, i) => (
              <span key={s.id} data-toc-target={s.id} className="block text-sm text-primary-600 py-0.5">
                {i + 6}. {t(s.key)}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 sm:p-8">

        {/* 1. Overview */}
        <SectionHeading id="overview" number={1} tKey="sections.overview.title" t={t} />
        <P>{t('sections.overview.body')}</P>

        {/* 2. Requirements */}
        <SectionHeading id="requirements" number={2} tKey="sections.requirements.title" t={t} />
        <PdfBulletList items={[t('sections.requirements.item1'), t('sections.requirements.item2')]} />

        {/* 3. Scientific Foundations */}
        <SectionHeading id="foundations" number={3} tKey="sections.foundations.title" t={t} />
        <P>{t('sections.foundations.body')}</P>
        <PdfBulletList items={[
          t('sections.foundations.item1'),
          t('sections.foundations.item2'),
          t('sections.foundations.item3'),
        ]} />

        {/* 4. Administrative Procedures */}
        <SectionHeading id="procedures" number={4} tKey="sections.procedures.title" t={t} />

        <SubHeading>{t('sections.procedures.accountSetup.title')}</SubHeading>
        <PdfNumberedList items={[
          t('sections.procedures.accountSetup.step1'),
          <Trans i18nKey="sections.procedures.accountSetup.step2" ns="manual" components={{ b: <strong /> }} key="step2" />,
          t('sections.procedures.accountSetup.step3'),
          <Trans i18nKey="sections.procedures.accountSetup.step4" ns="manual" components={{ b: <strong /> }} key="step4" />,
          t('sections.procedures.accountSetup.step5'),
        ]} />

        <SubHeading>{t('sections.procedures.createPair.title')}</SubHeading>
        <PdfNumberedList items={[
          <Trans i18nKey="sections.procedures.createPair.step1" ns="manual" components={{ b: <strong /> }} key="cp1" />,
          t('sections.procedures.createPair.step2'),
          t('sections.procedures.createPair.step3'),
          <><em>{t('sections.procedures.createPair.noteLabel')}</em> {t('sections.procedures.createPair.noteText')}</>,
          t('sections.procedures.createPair.step5'),
        ]} />

        <SubHeading>{t('sections.procedures.managePairs.title')}</SubHeading>
        <P>{t('sections.procedures.managePairs.body')}</P>
        <PdfBulletList items={[
          <><strong>{t('sections.procedures.managePairs.editLabel')}</strong> {t('sections.procedures.managePairs.editText')}</>,
          <><strong>{t('sections.procedures.managePairs.progressLabel')}</strong> {t('sections.procedures.managePairs.progressText')}</>,
          <><strong>{t('sections.procedures.managePairs.resendLabel')}</strong> {t('sections.procedures.managePairs.resendText')}</>,
          <><strong>{t('sections.procedures.managePairs.notificationLabel')}</strong> {t('sections.procedures.managePairs.notificationText')}</>,
        ]} />

        <SubHeading>{t('sections.procedures.linkValidity.title')}</SubHeading>
        <P><Trans i18nKey="sections.procedures.linkValidity.body" ns="manual" components={{ b: <strong /> }} /></P>

        <SubHeading>{t('sections.procedures.clientInstructions.title')}</SubHeading>
        <P>{t('sections.procedures.clientInstructions.body')}</P>
        <PdfBulletList items={[
          t('sections.procedures.clientInstructions.item1'),
          t('sections.procedures.clientInstructions.item2'),
          <Trans i18nKey="sections.procedures.clientInstructions.item3" ns="manual" components={{ b: <strong /> }} key="ci3" />,
        ]} />

        <SubHeading>{t('sections.procedures.accessingResults.title')}</SubHeading>
        <P>{t('sections.procedures.accessingResults.body')}</P>

        {/* 5. The Assessments */}
        <SectionHeading id="assessments" number={5} tKey="sections.assessments.title" t={t} />
        <P>{t('sections.assessments.body')}</P>

        <SubHeading>{t('sections.assessments.profile.title')}</SubHeading>
        <P>{t('sections.assessments.profile.body')}</P>

        <SubHeading>{t('sections.assessments.dci.title')}</SubHeading>
        <P>{t('sections.assessments.dci.body')}</P>
        <DataTable
          headers={[t('sections.assessments.dci.colAbbr'), t('sections.assessments.dci.colName'), t('sections.assessments.dci.colDesc')]}
          rows={[
            ['SCO', t('sections.assessments.dci.sco'), t('sections.assessments.dci.scoDesc')],
            ['SCP', t('sections.assessments.dci.scp'), t('sections.assessments.dci.scpDesc')],
            ['SDCP', t('sections.assessments.dci.sdcp'), t('sections.assessments.dci.sdcpDesc')],
            ['SDC', t('sections.assessments.dci.sdc'), t('sections.assessments.dci.sdcDesc')],
            ['DDCP', t('sections.assessments.dci.ddcp'), t('sections.assessments.dci.ddcpDesc')],
            ['DDCO', t('sections.assessments.dci.ddco'), t('sections.assessments.dci.ddcoDesc')],
            ['NDCP', t('sections.assessments.dci.ndcp'), t('sections.assessments.dci.ndcpDesc')],
            ['NDCO', t('sections.assessments.dci.ndco'), t('sections.assessments.dci.ndcoDesc')],
            ['CDC', t('sections.assessments.dci.cdc'), t('sections.assessments.dci.cdcDesc')],
            ['EDC', t('sections.assessments.dci.edc'), t('sections.assessments.dci.edcDesc')],
          ]}
        />
        <P><strong>{t('sections.assessments.dci.dcisLabel')}</strong></P>
        <DataTable
          headers={[t('sections.assessments.dci.colRange'), t('sections.assessments.dci.colCategory')]}
          rows={[
            [t('sections.assessments.dci.range1'), t('sections.assessments.dci.cat1')],
            [t('sections.assessments.dci.range2'), t('sections.assessments.dci.cat2')],
            [t('sections.assessments.dci.range3'), t('sections.assessments.dci.cat3')],
          ]}
        />

        <SubHeading>{t('sections.assessments.ipip.title')}</SubHeading>
        <P>{t('sections.assessments.ipip.body')}</P>
        <DataTable
          headers={[t('sections.assessments.ipip.colDomain'), t('sections.assessments.ipip.colFacets')]}
          rows={[
            [t('sections.assessments.ipip.neuroticism'), t('sections.assessments.ipip.neuroticismFacets')],
            [t('sections.assessments.ipip.conscientiousness'), t('sections.assessments.ipip.conscientiousnessFacets')],
            [t('sections.assessments.ipip.agreeableness'), t('sections.assessments.ipip.agreeablenessFacets')],
            [t('sections.assessments.ipip.extraversion'), t('sections.assessments.ipip.extraversionFacets')],
            [t('sections.assessments.ipip.openness'), t('sections.assessments.ipip.opennessFacets')],
          ]}
        />
        <P>{t('sections.assessments.ipip.scoring')}</P>

        {/* 6. Analysis and Interpretation */}
        <SectionHeading id="interpretation" number={6} tKey="sections.interpretation.title" t={t} />

        <SubHeading>{t('sections.interpretation.targets.title')}</SubHeading>
        <P>{t('sections.interpretation.targets.body')}</P>
        <PdfBulletList items={[
          <><strong>{t('sections.interpretation.targets.mssLabel')}</strong> {t('sections.interpretation.targets.mssText')}</>,
          <><strong>{t('sections.interpretation.targets.dcisLabel')}</strong> {t('sections.interpretation.targets.dcisText')}</>,
        ]} />
        <P>{t('sections.interpretation.targets.gapNote')}</P>

        <SubHeading>{t('sections.interpretation.steps.title')}</SubHeading>
        <PdfNumberedList start={1} items={[
          <><strong>{t('sections.interpretation.steps.step1Label')}</strong> {t('sections.interpretation.steps.step1Text')}</>,
          <><strong>{t('sections.interpretation.steps.step2Label')}</strong> {t('sections.interpretation.steps.step2Text')}</>,
        ]} />
        <DataTable
          headers={[t('sections.interpretation.steps.colRange'), t('sections.interpretation.steps.colCategory')]}
          rows={[
            [t('sections.interpretation.steps.mss1'), t('sections.interpretation.steps.mssCat1')],
            [t('sections.interpretation.steps.mss2'), t('sections.interpretation.steps.mssCat2')],
            [t('sections.interpretation.steps.mss3'), t('sections.interpretation.steps.mssCat3')],
            [t('sections.interpretation.steps.mss4'), t('sections.interpretation.steps.mssCat4')],
            [t('sections.interpretation.steps.mss5'), t('sections.interpretation.steps.mssCat5')],
          ]}
          className="ml-6"
        />
        <PdfNumberedList start={3} items={[
          <><strong>{t('sections.interpretation.steps.step3Label')}</strong> {t('sections.interpretation.steps.step3Text')}</>,
          <><strong>{t('sections.interpretation.steps.step4Label')}</strong> {t('sections.interpretation.steps.step4Text')}</>,
        ]} />
        <PdfBulletList className="ml-12" items={[
          <><strong>{t('sections.interpretation.steps.dciP1Label')}</strong> {t('sections.interpretation.steps.dciP1Text')}</>,
          <><strong>{t('sections.interpretation.steps.dciP2Label')}</strong> {t('sections.interpretation.steps.dciP2Text')}</>,
        ]} />
        <P><em>{t('sections.interpretation.steps.dciNote')}</em></P>

        <PdfNumberedList start={5} items={[
          <><strong>{t('sections.interpretation.steps.step5Label')}</strong> {t('sections.interpretation.steps.step5Text')}</>,
        ]} />
        <PdfBulletList className="ml-12" items={[
          <><strong>{t('sections.interpretation.steps.ipipP1Label')}</strong> {t('sections.interpretation.steps.ipipP1Text')}</>,
          <><strong>{t('sections.interpretation.steps.ipipP2Label')}</strong> {t('sections.interpretation.steps.ipipP2Text')}</>,
          <><strong>{t('sections.interpretation.steps.ipipP3Label')}</strong> {t('sections.interpretation.steps.ipipP3Text')}</>,
        ]} />

        <PdfNumberedList start={6} items={[
          <><strong>{t('sections.interpretation.steps.step6Label')}</strong> {t('sections.interpretation.steps.step6Text')}</>,
        ]} />

        {/* 7. Reading the Report */}
        <SectionHeading id="report" number={7} tKey="sections.report.title" t={t} />
        <P>{t('sections.report.body')}</P>

        <SubHeading>{t('sections.report.scoresTab.title')}</SubHeading>
        <P>{t('sections.report.scoresTab.body')}</P>
        <PdfNumberedList items={[
          t('sections.report.scoresTab.item1'),
          t('sections.report.scoresTab.item2'),
          t('sections.report.scoresTab.item3'),
          t('sections.report.scoresTab.item4'),
          t('sections.report.scoresTab.item5'),
          t('sections.report.scoresTab.item6'),
        ]} />

        <SubHeading>{t('sections.report.reportTab.title')}</SubHeading>
        <P>{t('sections.report.reportTab.body')}</P>
        <PdfBulletList items={[
          t('sections.report.reportTab.item1'),
          t('sections.report.reportTab.item2'),
          t('sections.report.reportTab.item3'),
          t('sections.report.reportTab.item4'),
        ]} />

        <SubHeading>{t('sections.report.pdfExport.title')}</SubHeading>
        <P>{t('sections.report.pdfExport.body')}</P>
        <PdfBulletList items={[
          <><strong>{t('sections.report.pdfExport.quantLabel')}</strong> {t('sections.report.pdfExport.quantText')}</>,
          <><strong>{t('sections.report.pdfExport.qualLabel')}</strong> {t('sections.report.pdfExport.qualText')}</>,
          <><strong>{t('sections.report.pdfExport.combinedLabel')}</strong> {t('sections.report.pdfExport.combinedText')}</>,
        ]} />

        {/* 8. Profile Management */}
        <SectionHeading id="profile" number={8} tKey="sections.profile.title" t={t} />
        <P>{t('sections.profile.body')}</P>
        <PdfBulletList items={[
          <><strong>{t('sections.profile.updateLabel')}</strong> {t('sections.profile.updateText')}</>,
          <><strong>{t('sections.profile.emailLabel')}</strong> {t('sections.profile.emailText')}</>,
          <><strong>{t('sections.profile.passwordLabel')}</strong> {t('sections.profile.passwordText')}</>,
        ]} />

        {/* 9. Clinical Guidance */}
        <SectionHeading id="clinical" number={9} tKey="sections.clinical.title" t={t} />
        <P>{t('sections.clinical.body')}</P>

        {/* 10. Troubleshooting */}
        <SectionHeading id="troubleshooting" number={10} tKey="sections.troubleshooting.title" t={t} />
        <DataTable
          headers={[t('sections.troubleshooting.colIssue'), t('sections.troubleshooting.colSolution')]}
          rows={[
            [t('sections.troubleshooting.issue1'), t('sections.troubleshooting.solution1')],
            [t('sections.troubleshooting.issue2'), t('sections.troubleshooting.solution2')],
            [t('sections.troubleshooting.issue3'), t('sections.troubleshooting.solution3')],
            [t('sections.troubleshooting.issue4'), t('sections.troubleshooting.solution4')],
            [t('sections.troubleshooting.issue5'), t('sections.troubleshooting.solution5')],
          ]}
        />
      </div>
    </>
  )
}

export default function FacilitatorManual() {
  const { t } = useTranslation('manual')
  const [exporting, setExporting] = useState(false)
  const [showOffscreen, setShowOffscreen] = useState(false)
  const offscreenRef = useRef(null)

  async function handleExportPdf() {
    setExporting(true)
    setShowOffscreen(true)

    // Wait for React to render the offscreen content
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)))

    try {
      // Measure TOC link and section heading positions for clickable PDF links
      const container = offscreenRef.current
      const containerRect = container.getBoundingClientRect()
      const internalLinks = []

      container.querySelectorAll('[data-toc-target]').forEach(el => {
        const targetId = el.getAttribute('data-toc-target')
        const targetEl = container.querySelector(`#${targetId}`)
        if (!targetEl) return

        const linkRect = el.getBoundingClientRect()
        const targetRect = targetEl.getBoundingClientRect()

        internalLinks.push({
          x: linkRect.left - containerRect.left,
          y: linkRect.top - containerRect.top,
          w: linkRect.width,
          h: linkRect.height,
          targetY: targetRect.top - containerRect.top,
        })
      })

      await generatePDF(container, {
        title: t('title'),
        filename: `csi-facilitator-manual-${new Date().toISOString().split('T')[0]}.pdf`,
        internalLinks,
        domWidth: containerRect.width,
        domHeight: containerRect.height,
      })
    } finally {
      setExporting(false)
      setShowOffscreen(false)
    }
  }

  return (
    <div className="animate-fadeIn max-w-4xl mx-auto">
      {/* Back link & Export button */}
      <div className="flex items-center justify-between mb-6">
        <Link to="/facilitator" className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700">
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('backToDashboard')}
        </Link>
        <button
          onClick={handleExportPdf}
          disabled={exporting}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg shadow-sm hover:from-primary-600 hover:to-primary-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
        >
          {exporting ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {t('common:pdfExport.generatingPdf')}
            </>
          ) : (
            <>
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {t('exportPdf')}
            </>
          )}
        </button>
      </div>

      {/* Visible content with anchor links */}
      <div className="text-center mb-10">
        <h1 className="font-display text-3xl font-semibold text-gray-800 mb-2">{t('title')}</h1>
        <p className="text-gray-500 text-sm">{t('subtitle')}</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
        <h2 className="font-display text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">{t('toc')}</h2>
        <nav className="sm:flex sm:gap-8">
          <div className="space-y-1.5 flex-1">
            {TOC_SECTIONS.slice(0, 5).map((s, i) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="block text-sm text-primary-600 hover:text-primary-700 hover:underline py-0.5"
              >
                {i + 1}. {t(s.key)}
              </a>
            ))}
          </div>
          <div className="space-y-1.5 flex-1 mt-1.5 sm:mt-0">
            {TOC_SECTIONS.slice(5).map((s, i) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="block text-sm text-primary-600 hover:text-primary-700 hover:underline py-0.5"
              >
                {i + 6}. {t(s.key)}
              </a>
            ))}
          </div>
        </nav>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 sm:p-8">
        {/* Inline the sections here for anchor link support */}
        <SectionHeading id="overview" number={1} tKey="sections.overview.title" t={t} />
        <P>{t('sections.overview.body')}</P>

        <SectionHeading id="requirements" number={2} tKey="sections.requirements.title" t={t} />
        <BulletList items={[t('sections.requirements.item1'), t('sections.requirements.item2')]} />

        <SectionHeading id="foundations" number={3} tKey="sections.foundations.title" t={t} />
        <P>{t('sections.foundations.body')}</P>
        <BulletList items={[
          t('sections.foundations.item1'),
          t('sections.foundations.item2'),
          t('sections.foundations.item3'),
        ]} />

        <SectionHeading id="procedures" number={4} tKey="sections.procedures.title" t={t} />
        <SubHeading>{t('sections.procedures.accountSetup.title')}</SubHeading>
        <NumberedList items={[
          t('sections.procedures.accountSetup.step1'),
          <Trans i18nKey="sections.procedures.accountSetup.step2" ns="manual" components={{ b: <strong /> }} key="step2" />,
          t('sections.procedures.accountSetup.step3'),
          <Trans i18nKey="sections.procedures.accountSetup.step4" ns="manual" components={{ b: <strong /> }} key="step4" />,
          t('sections.procedures.accountSetup.step5'),
        ]} />
        <SubHeading>{t('sections.procedures.createPair.title')}</SubHeading>
        <NumberedList items={[
          <Trans i18nKey="sections.procedures.createPair.step1" ns="manual" components={{ b: <strong /> }} key="cp1" />,
          t('sections.procedures.createPair.step2'),
          t('sections.procedures.createPair.step3'),
          <><em>{t('sections.procedures.createPair.noteLabel')}</em> {t('sections.procedures.createPair.noteText')}</>,
          t('sections.procedures.createPair.step5'),
        ]} />
        <SubHeading>{t('sections.procedures.managePairs.title')}</SubHeading>
        <P>{t('sections.procedures.managePairs.body')}</P>
        <BulletList items={[
          <><strong>{t('sections.procedures.managePairs.editLabel')}</strong> {t('sections.procedures.managePairs.editText')}</>,
          <><strong>{t('sections.procedures.managePairs.progressLabel')}</strong> {t('sections.procedures.managePairs.progressText')}</>,
          <><strong>{t('sections.procedures.managePairs.resendLabel')}</strong> {t('sections.procedures.managePairs.resendText')}</>,
          <><strong>{t('sections.procedures.managePairs.notificationLabel')}</strong> {t('sections.procedures.managePairs.notificationText')}</>,
        ]} />
        <SubHeading>{t('sections.procedures.linkValidity.title')}</SubHeading>
        <P><Trans i18nKey="sections.procedures.linkValidity.body" ns="manual" components={{ b: <strong /> }} /></P>
        <SubHeading>{t('sections.procedures.clientInstructions.title')}</SubHeading>
        <P>{t('sections.procedures.clientInstructions.body')}</P>
        <BulletList items={[
          t('sections.procedures.clientInstructions.item1'),
          t('sections.procedures.clientInstructions.item2'),
          <Trans i18nKey="sections.procedures.clientInstructions.item3" ns="manual" components={{ b: <strong /> }} key="ci3" />,
        ]} />
        <SubHeading>{t('sections.procedures.accessingResults.title')}</SubHeading>
        <P>{t('sections.procedures.accessingResults.body')}</P>

        <SectionHeading id="assessments" number={5} tKey="sections.assessments.title" t={t} />
        <P>{t('sections.assessments.body')}</P>
        <SubHeading>{t('sections.assessments.profile.title')}</SubHeading>
        <P>{t('sections.assessments.profile.body')}</P>
        <SubHeading>{t('sections.assessments.dci.title')}</SubHeading>
        <P>{t('sections.assessments.dci.body')}</P>
        <DataTable
          headers={[t('sections.assessments.dci.colAbbr'), t('sections.assessments.dci.colName'), t('sections.assessments.dci.colDesc')]}
          rows={[
            ['SCO', t('sections.assessments.dci.sco'), t('sections.assessments.dci.scoDesc')],
            ['SCP', t('sections.assessments.dci.scp'), t('sections.assessments.dci.scpDesc')],
            ['SDCP', t('sections.assessments.dci.sdcp'), t('sections.assessments.dci.sdcpDesc')],
            ['SDC', t('sections.assessments.dci.sdc'), t('sections.assessments.dci.sdcDesc')],
            ['DDCP', t('sections.assessments.dci.ddcp'), t('sections.assessments.dci.ddcpDesc')],
            ['DDCO', t('sections.assessments.dci.ddco'), t('sections.assessments.dci.ddcoDesc')],
            ['NDCP', t('sections.assessments.dci.ndcp'), t('sections.assessments.dci.ndcpDesc')],
            ['NDCO', t('sections.assessments.dci.ndco'), t('sections.assessments.dci.ndcoDesc')],
            ['CDC', t('sections.assessments.dci.cdc'), t('sections.assessments.dci.cdcDesc')],
            ['EDC', t('sections.assessments.dci.edc'), t('sections.assessments.dci.edcDesc')],
          ]}
        />
        <P><strong>{t('sections.assessments.dci.dcisLabel')}</strong></P>
        <DataTable
          headers={[t('sections.assessments.dci.colRange'), t('sections.assessments.dci.colCategory')]}
          rows={[
            [t('sections.assessments.dci.range1'), t('sections.assessments.dci.cat1')],
            [t('sections.assessments.dci.range2'), t('sections.assessments.dci.cat2')],
            [t('sections.assessments.dci.range3'), t('sections.assessments.dci.cat3')],
          ]}
        />
        <SubHeading>{t('sections.assessments.ipip.title')}</SubHeading>
        <P>{t('sections.assessments.ipip.body')}</P>
        <DataTable
          headers={[t('sections.assessments.ipip.colDomain'), t('sections.assessments.ipip.colFacets')]}
          rows={[
            [t('sections.assessments.ipip.neuroticism'), t('sections.assessments.ipip.neuroticismFacets')],
            [t('sections.assessments.ipip.conscientiousness'), t('sections.assessments.ipip.conscientiousnessFacets')],
            [t('sections.assessments.ipip.agreeableness'), t('sections.assessments.ipip.agreeablenessFacets')],
            [t('sections.assessments.ipip.extraversion'), t('sections.assessments.ipip.extraversionFacets')],
            [t('sections.assessments.ipip.openness'), t('sections.assessments.ipip.opennessFacets')],
          ]}
        />
        <P>{t('sections.assessments.ipip.scoring')}</P>

        <SectionHeading id="interpretation" number={6} tKey="sections.interpretation.title" t={t} />
        <SubHeading>{t('sections.interpretation.targets.title')}</SubHeading>
        <P>{t('sections.interpretation.targets.body')}</P>
        <BulletList items={[
          <><strong>{t('sections.interpretation.targets.mssLabel')}</strong> {t('sections.interpretation.targets.mssText')}</>,
          <><strong>{t('sections.interpretation.targets.dcisLabel')}</strong> {t('sections.interpretation.targets.dcisText')}</>,
        ]} />
        <P>{t('sections.interpretation.targets.gapNote')}</P>
        <SubHeading>{t('sections.interpretation.steps.title')}</SubHeading>
        <NumberedList items={[
          <><strong>{t('sections.interpretation.steps.step1Label')}</strong> {t('sections.interpretation.steps.step1Text')}</>,
          <><strong>{t('sections.interpretation.steps.step2Label')}</strong> {t('sections.interpretation.steps.step2Text')}</>,
        ]} />
        <DataTable
          headers={[t('sections.interpretation.steps.colRange'), t('sections.interpretation.steps.colCategory')]}
          rows={[
            [t('sections.interpretation.steps.mss1'), t('sections.interpretation.steps.mssCat1')],
            [t('sections.interpretation.steps.mss2'), t('sections.interpretation.steps.mssCat2')],
            [t('sections.interpretation.steps.mss3'), t('sections.interpretation.steps.mssCat3')],
            [t('sections.interpretation.steps.mss4'), t('sections.interpretation.steps.mssCat4')],
            [t('sections.interpretation.steps.mss5'), t('sections.interpretation.steps.mssCat5')],
          ]}
          className="ml-6"
        />
        <NumberedList items={[
          <><strong>{t('sections.interpretation.steps.step3Label')}</strong> {t('sections.interpretation.steps.step3Text')}</>,
          <><strong>{t('sections.interpretation.steps.step4Label')}</strong> {t('sections.interpretation.steps.step4Text')}</>,
        ]} />
        <ul className="list-disc list-outside ml-12 space-y-1 text-gray-600 mb-2">
          <li><strong>{t('sections.interpretation.steps.dciP1Label')}</strong> {t('sections.interpretation.steps.dciP1Text')}</li>
          <li><strong>{t('sections.interpretation.steps.dciP2Label')}</strong> {t('sections.interpretation.steps.dciP2Text')}</li>
        </ul>
        <P><em>{t('sections.interpretation.steps.dciNote')}</em></P>
        <NumberedList items={[
          <><strong>{t('sections.interpretation.steps.step5Label')}</strong> {t('sections.interpretation.steps.step5Text')}</>,
        ]} />
        <ul className="list-disc list-outside ml-12 space-y-1 text-gray-600 mb-2">
          <li><strong>{t('sections.interpretation.steps.ipipP1Label')}</strong> {t('sections.interpretation.steps.ipipP1Text')}</li>
          <li><strong>{t('sections.interpretation.steps.ipipP2Label')}</strong> {t('sections.interpretation.steps.ipipP2Text')}</li>
          <li><strong>{t('sections.interpretation.steps.ipipP3Label')}</strong> {t('sections.interpretation.steps.ipipP3Text')}</li>
        </ul>
        <NumberedList items={[
          <><strong>{t('sections.interpretation.steps.step6Label')}</strong> {t('sections.interpretation.steps.step6Text')}</>,
        ]} />

        <SectionHeading id="report" number={7} tKey="sections.report.title" t={t} />
        <P>{t('sections.report.body')}</P>
        <SubHeading>{t('sections.report.scoresTab.title')}</SubHeading>
        <P>{t('sections.report.scoresTab.body')}</P>
        <NumberedList items={[
          t('sections.report.scoresTab.item1'),
          t('sections.report.scoresTab.item2'),
          t('sections.report.scoresTab.item3'),
          t('sections.report.scoresTab.item4'),
          t('sections.report.scoresTab.item5'),
          t('sections.report.scoresTab.item6'),
        ]} />
        <SubHeading>{t('sections.report.reportTab.title')}</SubHeading>
        <P>{t('sections.report.reportTab.body')}</P>
        <BulletList items={[
          t('sections.report.reportTab.item1'),
          t('sections.report.reportTab.item2'),
          t('sections.report.reportTab.item3'),
          t('sections.report.reportTab.item4'),
        ]} />
        <SubHeading>{t('sections.report.pdfExport.title')}</SubHeading>
        <P>{t('sections.report.pdfExport.body')}</P>
        <BulletList items={[
          <><strong>{t('sections.report.pdfExport.quantLabel')}</strong> {t('sections.report.pdfExport.quantText')}</>,
          <><strong>{t('sections.report.pdfExport.qualLabel')}</strong> {t('sections.report.pdfExport.qualText')}</>,
          <><strong>{t('sections.report.pdfExport.combinedLabel')}</strong> {t('sections.report.pdfExport.combinedText')}</>,
        ]} />

        <SectionHeading id="profile" number={8} tKey="sections.profile.title" t={t} />
        <P>{t('sections.profile.body')}</P>
        <BulletList items={[
          <><strong>{t('sections.profile.updateLabel')}</strong> {t('sections.profile.updateText')}</>,
          <><strong>{t('sections.profile.emailLabel')}</strong> {t('sections.profile.emailText')}</>,
          <><strong>{t('sections.profile.passwordLabel')}</strong> {t('sections.profile.passwordText')}</>,
        ]} />

        <SectionHeading id="clinical" number={9} tKey="sections.clinical.title" t={t} />
        <P>{t('sections.clinical.body')}</P>

        <SectionHeading id="troubleshooting" number={10} tKey="sections.troubleshooting.title" t={t} />
        <DataTable
          headers={[t('sections.troubleshooting.colIssue'), t('sections.troubleshooting.colSolution')]}
          rows={[
            [t('sections.troubleshooting.issue1'), t('sections.troubleshooting.solution1')],
            [t('sections.troubleshooting.issue2'), t('sections.troubleshooting.solution2')],
            [t('sections.troubleshooting.issue3'), t('sections.troubleshooting.solution3')],
            [t('sections.troubleshooting.issue4'), t('sections.troubleshooting.solution4')],
            [t('sections.troubleshooting.issue5'), t('sections.troubleshooting.solution5')],
          ]}
        />
      </div>

      {/* Offscreen capture for PDF export */}
      {showOffscreen && (
        <div
          style={{ position: 'absolute', left: '-9999px', top: 0, width: '896px', fontFamily: "'Times New Roman', Times, serif" }}
          className="pdf-capture bg-white"
          aria-hidden="true"
        >
          <div ref={offscreenRef} className="p-6 pdf-capture">
            <ManualContent t={t} />
          </div>
        </div>
      )}
    </div>
  )
}
